/**
 * MinimaxAI - Main AI Orchestrator
 *
 * Entry point for V3 AI system. Coordinates strategic evaluation,
 * move generation, and minimax search to produce optimal decisions.
 */

import { GameState, FactionType, City, CityLevel } from '../../types';
import { UNIT_DEFINITIONS } from '../../unitDefinitions';
import { UnitManager } from '../../UnitManager';
import { GameMap } from '../../Map';
import {
  AIDecision,
  GameStateSnapshot,
  GeneratedMove,
  MoveType,
  StrategicPriority,
  FrontType,
  getSearchConfig
} from './V3Types';
import {
  createSnapshot,
  applyMove,
  getActiveUnits
} from './core/GameStateSnapshot';
import {
  generateProductionMoves,
  orderMoves
} from './core/MoveGenerator';
import { findBestMoveForUnit } from './core/MinimaxEngine';
import {
  analyzeStrategicPriority,
  StrategicAnalysis
} from './priority';
import {
  createFronts,
  getUnitObjective,
  getFrontsSummary,
  FrontAssignment
} from './core/FrontManager';

/**
 * Main AI class that produces decisions for a faction
 */
export class MinimaxAI {
  private gameState: GameState;
  private unitManager: UnitManager;
  private map: GameMap;
  private faction: FactionType;
  private enemyFaction: FactionType;
  private campaign?: 'normandy' | 'tunisia' | 'stalingrad' | 'okinawa' | 'campobasso' | 'tutorial' | 'france_1940' | 'albania_1940';

  // Decision queue for current turn
  private decisionQueue: AIDecision[] = [];
  private currentDecisionIndex: number = 0;

  // Front assignment for coordinated multi-objective attacks
  private frontAssignment: FrontAssignment | null = null;

  // Performance tracking
  private nodesEvaluated: number = 0;
  private searchDepth: number = 0;
  private searchStartTime: number = 0;

  constructor(
    gameState: GameState,
    unitManager: UnitManager,
    map: GameMap,
    faction: FactionType,
    enemyFaction: FactionType,
    campaign?: 'normandy' | 'tunisia' | 'stalingrad' | 'okinawa' | 'campobasso' | 'tutorial' | 'france_1940' | 'albania_1940'
  ) {
    this.gameState = gameState;
    this.unitManager = unitManager;
    this.map = map;
    this.faction = faction;
    this.enemyFaction = enemyFaction;
    this.campaign = campaign;
  }

  /**
   * Update references when game state changes
   */
  update(gameState: GameState, unitManager: UnitManager, map: GameMap): void {
    this.gameState = gameState;
    this.unitManager = unitManager;
    this.map = map;
  }

  /**
   * Plan the entire turn - generates queue of decisions using minimax search
   */
  planTurn(): void {
    this.decisionQueue = [];
    this.currentDecisionIndex = 0;
    this.nodesEvaluated = 0;
    this.searchDepth = 0;
    this.searchStartTime = Date.now();

    // Create snapshot of current state
    let snapshot = createSnapshot(
      this.gameState,
      this.unitManager,
      this.map,
      this.faction,
      this.enemyFaction,
      this.campaign
    );

    // Create front assignments for coordinated multi-objective attacks
    this.frontAssignment = createFronts(snapshot);
    console.log(`[V3 AI] Front assignments: ${getFrontsSummary(this.frontAssignment)}`);

    // Build unit objectives AND front types maps from front assignments
    const unitObjectives = new Map<string, { x: number; y: number }>();
    const unitFrontTypes = new Map<string, FrontType>();
    for (const unit of snapshot.units) {
      if (unit.faction === this.faction) {
        const objective = getUnitObjective(unit.id, this.frontAssignment, snapshot);
        if (objective) {
          unitObjectives.set(unit.id, objective.position);
          unitFrontTypes.set(unit.id, objective.type);
          // Log garrison assignments for debugging
          if (objective.type === FrontType.GARRISON) {
            console.log(`[V3 AI] Unit ${unit.type}@(${unit.position.x},${unit.position.y}) assigned to GARRISON`);
          }
        }
      }
    }

    // Update snapshot with unit objectives AND front types
    snapshot = {
      ...snapshot,
      unitObjectives,
      unitFrontTypes
    };

    // Analyze strategic context using priority system
    const context = this.analyzeStrategicContext(snapshot);
    console.log(`[V3 AI] Strategic priority: ${StrategicPriority[context.priority]}, Force ratio: ${context.forceRatio.toFixed(2)}`);
    console.log(`[V3 AI] Recommendation: ${context.recommendation}`);

    // Get search configuration based on unit count
    const activeUnits = getActiveUnits(snapshot, this.faction);
    console.log(`[V3 AI] Active units: ${activeUnits.length}`);

    const config = getSearchConfig(activeUnits.length);

    // Plan moves for all active units this turn
    // Each unit gets one action (move, attack, or move+attack)
    const unitsProcessed = new Set<string>();
    const turnTimeLimit = config.timeLimit * Math.max(1, activeUnits.length);

    for (const unit of activeUnits) {
      if ((Date.now() - this.searchStartTime) >= turnTimeLimit) {
        console.log(`[V3 AI] Time limit reached, stopping planning`);
        break;
      }

      if (unitsProcessed.has(unit.id)) continue;

      console.log(`[V3 AI] Planning for unit ${unit.type}@(${unit.position.x},${unit.position.y}) mv=${unit.movementRemaining} canAtk=${!unit.hasAttacked}`);

      // Find the best move for THIS SPECIFIC UNIT using minimax search
      const result = findBestMoveForUnit(snapshot, this.map, unit.id, {
        ...config,
        timeLimit: Math.min(config.timeLimit, turnTimeLimit - (Date.now() - this.searchStartTime))
      });

      this.nodesEvaluated += result.nodesSearched;
      this.searchDepth = Math.max(this.searchDepth, result.depth);

      console.log(`[V3 AI] Search result: move=${result.move?.type ?? 'null'}, to=${result.move?.to ? `(${result.move.to.x},${result.move.to.y})` : 'n/a'}, score=${result.score.toFixed(1)}, depth=${result.depth}`);

      // Mark this unit as processed regardless of whether we found a move
      unitsProcessed.add(unit.id);

      if (!result.move || result.move.type === MoveType.PASS) {
        console.log(`[V3 AI] No useful move found for unit, skipping`);
        continue;
      }

      // Convert to AIDecision and add to queue
      const decision = this.moveToDecision(result.move);
      if (decision) {
        console.log(`[V3 AI] Planned: ${decision.type} for unit ${unit.id}`);
        this.decisionQueue.push(decision);

        // For MOVE_AND_ATTACK, also queue the attack decision
        if (result.move.type === MoveType.MOVE_AND_ATTACK && result.move.targetId) {
          const targetUnit = this.unitManager.getUnit(result.move.targetId);
          if (targetUnit && unit) {
            const attackDecision: AIDecision = {
              type: 'attack',
              unit: this.unitManager.getUnit(result.move.unitId)!,
              targetUnit
            };
            console.log(`[V3 AI] Planned: attack (follow-up) for unit ${unit.id} -> ${targetUnit.id}`);
            this.decisionQueue.push(attackDecision);
          }
        }
        // For MOVE without attack, check if unit will have enemies in range after moving
        // If so, find the best target and queue an attack
        else if (result.move.type === MoveType.MOVE && result.move.to) {
          const unitDef = this.unitManager.getUnit(result.move.unitId);
          if (unitDef) {
            const newPos = result.move.to;
            const range = unitDef.stats.range;
            // Find enemies in range from the new position
            const enemiesInRange = this.unitManager.getAllUnits().filter(e =>
              e.faction === this.enemyFaction &&
              Math.max(Math.abs(e.position.x - newPos.x), Math.abs(e.position.y - newPos.y)) <= range
            );
            if (enemiesInRange.length > 0) {
              // Pick the best target (lowest health, or highest value)
              const bestTarget = enemiesInRange.sort((a, b) => {
                // Prefer killing blows
                const aCanKill = a.stats.health <= unitDef.stats.attack;
                const bCanKill = b.stats.health <= unitDef.stats.attack;
                if (aCanKill && !bCanKill) return -1;
                if (bCanKill && !aCanKill) return 1;
                // Then prefer low health
                return a.stats.health - b.stats.health;
              })[0];

              const attackDecision: AIDecision = {
                type: 'attack',
                unit: unitDef,
                targetUnit: bestTarget
              };
              console.log(`[V3 AI] Planned: attack (auto after move) for unit ${unit.id} -> ${bestTarget.id}`);
              this.decisionQueue.push(attackDecision);
            }
          }
        }

        // Apply the move to our snapshot so subsequent units see updated positions
        snapshot = applyMove(snapshot, result.move);
      } else {
        console.log(`[V3 AI] Failed to convert move to decision`);
      }
    }

    // Add production decisions
    const productionDecisions = this.planProduction(snapshot, context);
    this.decisionQueue.push(...productionDecisions);

    // Always end with end_turn
    this.decisionQueue.push({ type: 'end_turn' });

    const totalTime = Date.now() - this.searchStartTime;
    console.log(`[V3 AI] Planned ${this.decisionQueue.length - 1} actions, searched ${this.nodesEvaluated} nodes to depth ${this.searchDepth} in ${totalTime}ms`);
  }

  /**
   * Get the next decision from the queue
   */
  getNextDecision(): AIDecision | null {
    if (this.currentDecisionIndex >= this.decisionQueue.length) {
      return null;
    }
    return this.decisionQueue[this.currentDecisionIndex++];
  }

  /**
   * Check if there are more decisions in the queue
   */
  hasMoreDecisions(): boolean {
    return this.currentDecisionIndex < this.decisionQueue.length;
  }

  /**
   * Analyze the strategic situation using the priority system
   */
  private analyzeStrategicContext(snapshot: GameStateSnapshot): StrategicAnalysis {
    return analyzeStrategicPriority(snapshot, this.faction, this.enemyFaction);
  }

  /**
   * Plan production based on strategic context
   * Tracks resource spending to avoid attempting more than we can afford
   */
  private planProduction(snapshot: GameStateSnapshot, context: StrategicAnalysis): AIDecision[] {
    const decisions: AIDecision[] = [];
    // Pass strategic priority to production - EXPANSION mode triggers special handling!
    const productionMoves = generateProductionMoves(snapshot, this.map, context.priority);

    // Order by priority and take top production moves
    const orderedProduction = orderMoves(productionMoves);

    // Track remaining resources as we plan production
    let remainingResources = snapshot.resources.get(snapshot.currentFaction) || 0;

    for (const move of orderedProduction) {
      if (decisions.length >= 3) break; // Max 3 production actions

      // Calculate cost of this move
      let cost = 0;
      if (move.type === MoveType.BUILD && move.unitType) {
        const def = UNIT_DEFINITIONS[move.unitType];
        cost = def.cost;
      } else if (move.type === MoveType.UPGRADE_CITY && move.upgradeLevel) {
        cost = move.upgradeLevel === CityLevel.FACTORY ? 25 : 50;  // Factory=25, Airbase=50
      }

      // Only add if we can afford it
      if (cost <= remainingResources) {
        const decision = this.moveToDecision(move);
        if (decision) {
          decisions.push(decision);
          remainingResources -= cost;
          console.log(`[V3 AI] Planned production: ${move.type}, cost=${cost}, remaining=${remainingResources}`);
        }
      }
    }

    return decisions;
  }

  /**
   * Convert a GeneratedMove to an AIDecision
   */
  private moveToDecision(move: GeneratedMove): AIDecision | null {
    // Get the actual Unit object from the game
    const unit = this.unitManager.getUnit(move.unitId);

    switch (move.type) {
      case MoveType.MOVE:
        if (!unit || !move.to) return null;
        return {
          type: 'move',
          unit,
          target: move.to
        };

      case MoveType.ATTACK:
        if (!unit || !move.targetId) return null;
        const targetUnit = this.unitManager.getUnit(move.targetId);
        if (!targetUnit) return null;
        return {
          type: 'attack',
          unit,
          targetUnit
        };

      case MoveType.MOVE_AND_ATTACK:
        // For move+attack, we return the move first
        // The attack will be issued separately when the unit can attack
        if (!unit || !move.to) return null;
        return {
          type: 'move',
          unit,
          target: move.to
        };

      case MoveType.HUNKER:
        if (!unit) return null;
        return {
          type: 'hunker',
          unit
        };

      case MoveType.HEAL:
        if (!unit || !move.targetId) return null;
        const healTarget = this.unitManager.getUnit(move.targetId);
        if (!healTarget) return null;
        return {
          type: 'heal',
          unit,
          targetUnit: healTarget
        };

      case MoveType.REPAIR:
        if (!unit || !move.targetId) return null;
        const repairTarget = this.unitManager.getUnit(move.targetId);
        if (!repairTarget) return null;
        return {
          type: 'repair',
          unit,
          targetUnit: repairTarget
        };

      case MoveType.BUILD:
        if (!move.cityName || !move.unitType) return null;
        const buildCity = this.findCityByName(move.cityName);
        if (!buildCity) return null;
        return {
          type: 'build',
          city: buildCity,
          unitType: move.unitType
        };

      case MoveType.UPGRADE_CITY:
        if (!move.cityName || !move.upgradeLevel) return null;
        const upgradeCity = this.findCityByName(move.cityName);
        if (!upgradeCity) return null;
        return {
          type: 'upgrade_city',
          city: upgradeCity,
          upgradeToLevel: move.upgradeLevel
        };

      case MoveType.BOARD_TRANSPORT:
        if (!unit || !move.targetId) return null;
        const transport = this.unitManager.getUnit(move.targetId);
        if (!transport) return null;
        return {
          type: 'board_transport',
          unit,
          targetUnit: transport
        };

      case MoveType.DISEMBARK:
        if (!unit || !move.to) return null;
        return {
          type: 'disembark',
          unit,
          target: move.to
        };

      case MoveType.TOW_ARTILLERY:
        if (!unit || !move.targetId) return null;
        const artillery = this.unitManager.getUnit(move.targetId);
        if (!artillery) return null;
        return {
          type: 'tow_artillery',
          unit,
          targetUnit: artillery
        };

      case MoveType.UNTOW_ARTILLERY:
        if (!unit) return null;
        return {
          type: 'untow_artillery',
          unit
        };

      case MoveType.PASS:
      default:
        return null;
    }
  }

  /**
   * Find a city by name
   */
  private findCityByName(name: string): City | null {
    for (const city of this.map.getCities().values()) {
      if (city.name === name) {
        return city;
      }
    }
    return null;
  }

  /**
   * Get debug info about the last planning phase
   */
  getDebugInfo(): string {
    return `V3 AI: ${this.decisionQueue.length - 1} actions planned, ${this.nodesEvaluated} nodes searched to depth ${this.searchDepth}`;
  }

  /**
   * Get AI state for saving (compatibility method)
   * Returns empty object for now - V3 AI doesn't need state persistence yet
   */
  getAIState(): object {
    return {
      faction: this.faction,
      enemyFaction: this.enemyFaction,
      version: 'v3'
    };
  }

  /**
   * Set tutorial objective (compatibility method)
   * V3 AI handles tutorial mode the same as normal play
   */
  setTutorialObjective(_objective: string, _targets?: any[], _mode?: string): void {
    // V3 AI doesn't use tutorial objectives - plays optimally regardless
    console.log(`[V3 AI] Tutorial objective set (ignored in V3): ${_objective}`);
  }

  /**
   * Enable tutorial production (compatibility method)
   */
  enableTutorialProduction(): void {
    // V3 AI always has production enabled
    console.log('[V3 AI] Tutorial production enabled (no-op in V3)');
  }

  /**
   * Restore AI state (compatibility method for save/load)
   */
  restoreAIState(_state: object): void {
    // V3 AI doesn't need state restoration yet
    console.log('[V3 AI] State restoration called (no-op in V3)');
  }
}

/**
 * Factory function to create a new MinimaxAI instance
 */
export function createMinimaxAI(
  gameState: GameState,
  unitManager: UnitManager,
  map: GameMap,
  faction: FactionType,
  enemyFaction: FactionType,
  campaign?: 'normandy' | 'tunisia' | 'stalingrad' | 'okinawa' | 'campobasso' | 'tutorial' | 'france_1940' | 'albania_1940'
): MinimaxAI {
  return new MinimaxAI(gameState, unitManager, map, faction, enemyFaction, campaign);
}
